import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import { Row, Col } from 'reactstrap';
import ReactHtmlParser from 'react-html-parser';
import { useIntl } from 'react-intl';
import { useResponsiveBackgroundImage } from '@whitelabel/helpers/hooks';
import { IImageBlock, IXCMSImage, Alignment } from '@whitelabel/helpers/types';
import { transformLink } from '../../../helpers';
import { StyledTitle } from '../HeadingBlock/styled';
import ButtonsBlock from '../ButtonsBlock';
import {
  StyledHeroBlock,
  StyledBGContainer,
  StyledImagesBlock,
  StyledTitleContainer,
  StyledContainer,
  StyledCol,
  StyledWrapper,
  StyledLeadText,
  StyledAdditionalComponentWrapper,
  StyledCTABlock,
} from './styled';
import DropdownLinkBlock from './DropdownLinksBlock';
import ConnectedGeoMultiQuotes from './GeoMultiQuotes';

interface IHeroBlockProps {
  title: string;
  leadText?: string;
  backgroundImage?: IXCMSImage;
  supportingImages?: {
    alignment?: Alignment;
    images?: IImageBlock[];
  };
  /*
    (ts-migrate) TODO: Migrate the remaining prop types
    ...contentBlockPropTypes
    */
  ctaOptions: any[];
  className?: string;
  headingWidth?: string;
  additionalComponent?: {
    id: string;
    type: string;
    value?: {
      component?: string;
      width?: string;
    };
  }[];
  id?: string;
}

const HeroBlock = ({
  title,
  leadText = '',
  backgroundImage,
  supportingImages,
  ctaOptions,
  className,
  id = '',
  headingWidth = '7',
  additionalComponent = [],
}: IHeroBlockProps): JSX.Element => {
  const [userEnteredPostcode, setUserEnteredPostcode] = useState('');
  const intl = useIntl();
  const hasAdditionalComponent = additionalComponent && additionalComponent.length > 0;
  const bgImage = useResponsiveBackgroundImage({ backgroundImage });
  const theme = useTheme();
  const isMobile = !useMediaQuery({ minWidth: theme.gridBreakpoints.sm });

  const renderTitle = () => (
    <StyledTitle>
      <span>{ReactHtmlParser(title, { transform: transformLink(intl) })}</span>
    </StyledTitle>
  );

  const renderLeadText = () => (
    <StyledLeadText>{ReactHtmlParser(leadText, { transform: transformLink(intl) })}</StyledLeadText>
  );

  const renderCTAOptions = () => (
    <StyledWrapper>
      {ctaOptions.map(({ type, value }: any) =>
        type === 'dropdown_links' ? (
          <DropdownLinkBlock key={type} {...value} />
        ) : (
          <ButtonsBlock key={type} {...value} />
        ),
      )}
    </StyledWrapper>
  );

  const renderAdditionalComponent = () => {
    const component = additionalComponent[0].value?.component;
    switch (component) {
      case 'multi_quotes':
        return (
          <StyledAdditionalComponentWrapper>
            <ConnectedGeoMultiQuotes
              userEnteredPostcode={userEnteredPostcode}
              setUserEnteredPostcode={setUserEnteredPostcode}
            />
          </StyledAdditionalComponentWrapper>
        );
      default:
        return null;
    }
  };

  const renderImagesBlock = () =>
    supportingImages &&
    supportingImages.images &&
    !!supportingImages.images.length && <StyledImagesBlock images={supportingImages.images} />;

  const renderMobileDOM = () => {
    const renderContent = () => (
      <>
        {hasAdditionalComponent && renderAdditionalComponent()}
        {renderLeadText()}
        {!!ctaOptions.length && renderCTAOptions()}
        {renderImagesBlock()}
      </>
    );
    return (
      <StyledHeroBlock id={id} className={className}>
        <StyledBGContainer $rtl={intl.bidi} $backgroundImageUrl={bgImage || null}>
          <StyledTitleContainer>
            <div>{renderTitle()}</div>
          </StyledTitleContainer>
        </StyledBGContainer>
        <StyledContainer>{renderContent()}</StyledContainer>
      </StyledHeroBlock>
    );
  };

  const renderOneColumnLayout = () => (
    <>
      <Row>
        <Col sm="9">{renderTitle()}</Col>
      </Row>
      <StyledWrapper as={Row}>
        <StyledCol sm="7">{renderLeadText()}</StyledCol>
      </StyledWrapper>
      <Row>
        <StyledCol sm="8">
          {renderCTAOptions()}
          {renderImagesBlock()}
        </StyledCol>
      </Row>
    </>
  );

  const renderWithAdditionalComp = () => (
    <Row>
      <StyledCol sm="6" lg={headingWidth}>
        {renderTitle()}
        {renderLeadText()}
        {!!ctaOptions.length && <StyledCTABlock>{renderCTAOptions()}</StyledCTABlock>}
        {renderImagesBlock()}
      </StyledCol>
      <StyledCol sm="6" lg={additionalComponent[0].value?.width}>
        {renderAdditionalComponent()}
      </StyledCol>
    </Row>
  );

  const renderNonMobileDOM = () => (
    <StyledHeroBlock id={id} className={className}>
      <StyledBGContainer $rtl={intl.bidi} $backgroundImageUrl={bgImage || null}>
        <StyledContainer>
          {hasAdditionalComponent ? renderWithAdditionalComp() : renderOneColumnLayout()}
        </StyledContainer>
      </StyledBGContainer>
    </StyledHeroBlock>
  );

  return isMobile ? renderMobileDOM() : renderNonMobileDOM();
};

export default HeroBlock;
